import { ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { SortedField, SortOrder } from "../../../shared/sort";
import { className } from "../../../shared/helpers";
import { IComponent } from "../../../services/http/component/models/IComponent";
import ComponentsList from "../../../components/Shared/ComponentsList";
import Header from "./Header";
import Filter from "./Filter";
import Sorting from "./Sorting";
import styles from './SideNav.module.scss';

interface SideNavProps
{
    rootComponent: IComponent;
    rootComponentChildren: IComponent[];
}

const SideNav = (props: SideNavProps): ReactElement | null =>
{
    const [filter, setFilter] = useState("");
    const [sidenavCollapsed, setSidenavCollapsed] = useState(true);
    const [sortedField, setSortedField] = useState(SortedField.empty);
    const history = useHistory();

    function filterComponents(data: IComponent[], filter: string): IComponent[]
    {
        return data.filter(item => !item?.name || !filter || item.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0);
    }

    function sortComponents(data: IComponent[], sortedField: SortedField): IComponent[]
    {
        if (sortedField.order === SortOrder.None) return data;

        switch (sortedField.caption)
        {
            case "Name":
                return data.sort((a, b) => (sortedField.order === SortOrder.Asc ? 1 : -1) * ((a.name > b.name) ? 1 : -1));
            case "Health":
                return data.sort((a, b) => (sortedField.order === SortOrder.Asc ? 1 : -1) * (a.healthIndicatorScore - b.healthIndicatorScore));
            default:
                return data;
        }
    }

    useEffect(() =>
    {
        const unlisten = history.listen(() => setSidenavCollapsed(true));
        return () => unlisten();
    }, [history]);

    const components = sortComponents(filterComponents(props.rootComponentChildren, filter), sortedField);
    const sortingFields = ["Name", "Health"];

    return (
        <aside {...className(styles.sidenav, { [styles.isCollapsed]: sidenavCollapsed })}>
            <Header
                component={props.rootComponent}
                sidenavHidden={sidenavCollapsed}
                setSidenavHidden={setSidenavCollapsed}
            />

            <Filter data={components} filter={filter} onFilter={setFilter} />

            <Sorting fields={sortingFields} sortedField={sortedField} onSorting={setSortedField} />

            <ComponentsList
                {...className(styles.componentsList, "scroll-container")}
                components={components}
                allowTooltips={true}
                tooltipClass={styles.tooltip}
            />
        </aside>
    );
}

export default SideNav;